import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DragIndicator from "@mui/icons-material/DragIndicator";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import RemoveOutlined from "@mui/icons-material/RemoveOutlined";
import UploadArea from "./UploadArea";
import MentionPromptInput from "./MentionPromptInput";
import { collectMentionableAssets } from "../mentionAssets";
import {
  addSceneToForm,
  removeSceneFromForm,
  reorderScenesInForm,
  seedScenesFromPrompt,
  stepSceneDurationInForm,
  totalScenesDuration,
} from "../scenePlan";
import {
  formatDurationLabel,
  getClipLimitForModel,
  MAX_DURATION_SEC,
} from "../durationOptions";

/**
 * Per-scene prompts for long videos (auto-extend / stitch).
 * Not Kling native multi_shots — each scene is a separate generation.
 */
export default function ScenePlanEditor({
  form,
  onChange,
  lang,
  onGenerateScene,
  generatingScenes,
  sceneStatuses,
}) {
  const theme = useTheme();
  const scenes = Array.isArray(form.scenes) ? form.scenes : [];
  const [dragFrom, setDragFrom] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(null);
  const busySet = generatingScenes instanceof Set ? generatingScenes : new Set(generatingScenes || []);

  if (!scenes.length) return null;

  const clipLimit = getClipLimitForModel(form.model, {
    hailuoResolution: form.hailuoResolution,
    resolution: form.resolution,
  });
  const clipMax = clipLimit.maxSec;
  const totalSec = totalScenesDuration(scenes) || Number(form.duration) || 0;
  const canAdd =
    MAX_DURATION_SEC - totalSec >= clipLimit.minSec &&
    scenes.length < 40;
  const canRemove = scenes.length > 1;

  const setScenes = (next) => onChange({ ...form, scenes: next });

  const resplit = () => {
    const durations = scenes.map((s) => Number(s.duration) || clipMax || 8);
    const seeded = seedScenesFromPrompt(form.prompt, durations);
    setScenes(
      seeded.map((s, i) => ({
        ...s,
        imageUrlUploads: scenes[i]?.imageUrlUploads || [],
      })),
    );
  };

  const handleAdd = () => {
    const next = addSceneToForm(form);
    if (next) onChange(next);
  };

  const handleRemove = (idx) => {
    if (!canRemove) return;
    onChange(removeSceneFromForm(form, idx));
  };

  const handleStepDuration = (idx, direction) => {
    onChange(stepSceneDurationInForm(form, idx, direction));
  };

  const sceneDurationBounds = (idx) => {
    const others = totalScenesDuration(scenes.filter((_, i) => i !== idx));
    const budget = Math.max(0, MAX_DURATION_SEC - others);
    const maxSec = Math.min(clipLimit.maxSec ?? MAX_DURATION_SEC, budget);
    const minSec = clipLimit.minSec;
    const current = Number(scenes[idx]?.duration) || minSec;
    return { minSec, maxSec, current, canDec: current > minSec, canInc: current < maxSec };
  };

  const handleDrop = (toIndex) => {
    if (dragFrom == null || dragFrom === toIndex) {
      setDragFrom(null);
      setDragOver(null);
      return;
    }
    onChange(reorderScenesInForm(form, dragFrom, toIndex));
    setDragFrom(null);
    setDragOver(null);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={800}>
              {lang?.videoStudioScenesTitle || "Scenes"}
            </Typography>
            <Typography variant="caption" color="primary.main" fontWeight={800}>
              {scenes.length} · {formatDurationLabel(totalSec)}{" "}
              {lang?.videoStudioScenesTotal || "total"}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.25, lineHeight: 1.4 }}
          >
            {(
              lang?.videoStudioScenesHint ||
              "Each scene is generated separately (max {clip}s). Use − / + to set length — the total duration updates. Drag to reorder."
            ).replace("{clip}", String(clipMax || "—"))}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<AutoFixHighOutlined />}
            onClick={resplit}
            sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
          >
            {lang?.videoStudioScenesResplit || "Re-split from brief"}
          </Button>
        </Stack>
      </Stack>

      {scenes.map((scene, idx) => {
        const isDragTarget = dragOver === idx && dragFrom !== idx;
        const bounds = sceneDurationBounds(idx);
        return (
          <Box
            key={scene.id || `scene-${idx + 1}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (dragFrom != null && dragFrom !== idx) setDragOver(idx);
            }}
            onDragLeave={() => {
              if (dragOver === idx) setDragOver(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(idx);
            }}
            sx={{
              p: 1.75,
              borderRadius: 2,
              border: "1px solid",
              borderColor: isDragTarget ? "primary.main" : "divider",
              bgcolor: isDragTarget
                ? alpha(theme.palette.primary.main, 0.08)
                : "action.hover",
              boxShadow: isDragTarget ? theme.shadows[2] : "none",
              transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
              opacity: dragFrom === idx ? 0.55 : 1,
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                  <Tooltip title={lang?.videoStudioSceneDrag || "Drag to reorder"}>
                    <IconButton
                      size="small"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(idx));
                        setDragFrom(idx);
                      }}
                      onDragEnd={() => {
                        setDragFrom(null);
                        setDragOver(null);
                      }}
                      aria-label={lang?.videoStudioSceneDrag || "Drag to reorder"}
                      sx={{
                        cursor: "grab",
                        color: "text.secondary",
                        "&:active": { cursor: "grabbing" },
                      }}
                    >
                      <DragIndicator fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="subtitle2" fontWeight={800}>
                    {(lang?.videoStudioSceneLabel || "Scene {n}").replace(
                      "{n}",
                      String(idx + 1),
                    )}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.background.paper, 0.6),
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip title={lang?.videoStudioSceneDurationDec || "Shorter"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!bounds.canDec || busySet.has(idx + 1)}
                          onClick={() => handleStepDuration(idx, -1)}
                          aria-label={lang?.videoStudioSceneDurationDec || "Decrease duration"}
                          sx={{ borderRadius: 0, width: 26, height: 26 }}
                        >
                          <RemoveOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      color="primary.main"
                      sx={{
                        minWidth: 36,
                        textAlign: "center",
                        px: 0.25,
                        userSelect: "none",
                      }}
                      title={
                        lang?.videoStudioSceneDurationHint ||
                        `Clip length (${bounds.minSec}–${bounds.maxSec}s)`
                      }
                    >
                      {formatDurationLabel(bounds.current)}
                    </Typography>
                    <Tooltip title={lang?.videoStudioSceneDurationInc || "Longer"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!bounds.canInc || busySet.has(idx + 1)}
                          onClick={() => handleStepDuration(idx, 1)}
                          aria-label={lang?.videoStudioSceneDurationInc || "Increase duration"}
                          sx={{ borderRadius: 0, width: 26, height: 26 }}
                        >
                          <AddOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  {typeof onGenerateScene === "function" ? (
                    <Tooltip
                      title={
                        busySet.has(idx + 1)
                          ? lang?.videoStudioSceneGenerating || "Generating this scene…"
                          : lang?.videoStudioSceneGenerate || "Generate this scene"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={busySet.has(idx + 1)}
                          onClick={() => onGenerateScene(idx)}
                          aria-label={lang?.videoStudioSceneGenerate || "Generate scene"}
                          sx={{
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                            "&:hover": {
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                            },
                          }}
                        >
                          {busySet.has(idx + 1) ? (
                            <CircularProgress size={16} thickness={5} />
                          ) : (
                            <PlayArrowOutlined fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  {sceneStatuses?.[idx + 1] === "ready" ? (
                    <Typography variant="caption" fontWeight={700} color="success.main">
                      {lang?.videoStudioSceneReady || "Ready"}
                    </Typography>
                  ) : sceneStatuses?.[idx + 1] === "failed" ? (
                    <Typography variant="caption" fontWeight={700} color="error.main">
                      {lang?.failed || "Failed"}
                    </Typography>
                  ) : null}
                  <Tooltip
                    title={
                      canRemove
                        ? lang?.videoStudioSceneRemove || "Remove scene"
                        : lang?.videoStudioSceneRemoveDisabled ||
                          "Keep at least one scene"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canRemove}
                        onClick={() => handleRemove(idx)}
                        aria-label={lang?.videoStudioSceneRemove || "Remove scene"}
                        sx={{ color: "text.secondary" }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              <UploadArea
                lang={lang}
                enableVoiceClip
                label={lang?.videoStudioSceneCharacters || "Characters / reference frames"}
                hint={
                  lang?.videoStudioSceneCharactersHint ||
                  "Upload character/reference images (up to 5). Tap the mic under each to set their voice — Dialogue lines are spoken in that voice."
                }
                files={scene.imageUrlUploads || []}
                onChange={(imageUrlUploads) => {
                  const next = [...scenes];
                  next[idx] = { ...next[idx], imageUrlUploads };
                  setScenes(next);
                }}
                maxFiles={5}
                disabled={busySet.has(idx + 1)}
              />
              <MentionPromptInput
                label={lang?.videoStudioScenePromptLabel || "Scene prompt / dialogue"}
                placeholder={
                  lang?.videoStudioScenePromptPlaceholder ||
                  "Dialogue: Hello…  ·  Voiceover: …  ·  John: …  ·  @Image: spoken line"
                }
                helperText={
                  lang?.videoStudioScenePromptHint ||
                  "Spoken line for this scene. Use Dialogue:, Voiceover:, a name (John:), or @Image: your line. Mentions stay visual — only the words after : are spoken."
                }
                value={scene.prompt || ""}
                onChange={(prompt) => {
                  const next = [...scenes];
                  next[idx] = { ...next[idx], prompt };
                  setScenes(next);
                }}
                assets={collectMentionableAssets(form, { sceneIndex: idx })}
                minRows={3}
                lang={lang}
              />
            </Stack>
          </Box>
        );
      })}

      <Tooltip
        title={
          canAdd
            ? lang?.videoStudioSceneAddHint || "Adds one clip and increases total duration"
            : lang?.videoStudioSceneAddMax ||
              `Reached the ${formatDurationLabel(MAX_DURATION_SEC)} maximum`
        }
      >
        <span>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddOutlined />}
            disabled={!canAdd}
            onClick={handleAdd}
            sx={{ fontWeight: 800, py: 1.1, borderStyle: "dashed" }}
          >
            {lang?.videoStudioSceneAdd || "Add scene"}
            {canAdd
              ? ` (+${formatDurationLabel(clipMax || clipLimit.minSec || 8)})`
              : ""}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
